import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  IconAI,
  Notice,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import { useCallback, useMemo } from 'react';

import { useSignedInDashboardContext } from '../../SignedInDashboardContextProvider';
import { CareerFeedAIAssistantContentFrame } from '../CareerFeedAIAssistantContent/CareerFeedAIAssistantContentFrame';
import { useGetResetCandidateChatContext } from '../hooks/candidateChatContextHelpers';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';
import type { ErrorType } from '../types';

import translations from './.vocab';

import * as styleRefs from './CareerFeedAiAssistantInitialState.css';

const useHeadMessage = () => {
  const { progress } = useCareerFeedAIAssistant();
  const { t } = useTranslations(translations);

  return useMemo(() => {
    switch (progress) {
      case 'initial':
      case 'gatheringRefinement':
      case 'reviewRefinement':
        return t(
          "Fine-tune your job recommendations – tell us what you're looking for with seekAI",
        );

      case 'recsRefined':
      case 'recsRefined-withPersistedData':
        return t(
          "We've updated your recommendations based on your answers. Take a look at the personalised job listings.",
        );
    }
  }, [progress, t]);
};

const CareerFeedAiAssistantInitialStateView = ({
  isButtonLoading,
  errorType,
  onButtonClick,
  hasRefinedRecs,
  resetPreferences,
}: {
  isButtonLoading: boolean;
  errorType?: ErrorType | null;
  onButtonClick: () => void;
  hasRefinedRecs: boolean;
  resetPreferences: () => void;
}) => {
  const { t } = useTranslations(translations);
  const headMessage = useHeadMessage();

  return (
    <CareerFeedAIAssistantContentFrame
      headMessage={{
        text: headMessage,
        size: 'small',
      }}
      contentSection={
        hasRefinedRecs ? (
          <Box className={styleRefs.disableStretch}>
            <Button size="small" onClick={resetPreferences}>
              {t('Reset my preferences')}
            </Button>
          </Box>
        ) : null
      }
      errorType={errorType}
      ctaSection={
        <Stack space="small">
          <Button
            tone="brandAccent"
            icon={<IconAI />}
            loading={isButtonLoading}
            onClick={onButtonClick}
          >
            {t('Start fine-tuning')}
          </Button>
          <Notice tone="info">
            <Text size="small">
              {t('By providing your personal information....', {
                CollectionNoticeLink: (children) => (
                  <TextLink
                    href="/collection-notice"
                    weight="weak"
                    target="_blank"
                  >
                    {children}
                  </TextLink>
                ),
                PrivacyLink: (children) => (
                  <TextLink href="/privacy" weight="weak" target="_blank">
                    {children}
                  </TextLink>
                ),
              })}
            </Text>
          </Notice>
        </Stack>
      }
    />
  );
};

export const CareerFeedAiAssistantInitialState = () => {
  const {
    invokeCreateSession,
    isCreateSessionLoading,
    createSessionError,
    persistedCandidateChatContext,
  } = useCareerFeedAIAssistant();
  const {
    recommendationsResult: { rawRecommendedJobs },
  } = useSignedInDashboardContext();

  const recommendedJobIds = useMemo(
    () => rawRecommendedJobs?.globalJobs.map((job) => job.id) ?? [],
    [rawRecommendedJobs],
  );

  const startSession = useCallback(() => {
    invokeCreateSession(recommendedJobIds);
  }, [invokeCreateSession, recommendedJobIds]);

  const resetPreferences = useGetResetCandidateChatContext();

  return (
    <CareerFeedAiAssistantInitialStateView
      isButtonLoading={isCreateSessionLoading}
      errorType={createSessionError}
      onButtonClick={startSession}
      hasRefinedRecs={Boolean(persistedCandidateChatContext)}
      resetPreferences={resetPreferences}
    />
  );
};
